import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  BaseButton,
  useAuth,
  useLoginRequiredModal,
  trackPathfinderOnboardingView,
} from '@repo/common';
import { getLatestPathfinderSession } from '../../api/pathfinder/pathfinder.api';
import PathfinderResult from './PathfinderResult';
import { clearPathfinderDraft } from './Pathfinder';

// 어드민 권한 판별용 role 값 (MinimalGnb와 동일 기준)
const ADMIN_ROLES = ['A', 'ROLE_A', 'ADMIN', 'ROLE_ADMIN'];

// 설문(온보딩) 완료 판단 — PathfinderDashboard와 동일 기준
const hasSavedScore = (session) =>
  session?.finalScore !== null && session?.finalScore !== undefined;
const hasCompletedOnboarding = (session) =>
  session?.status === 'COMPLETED' || hasSavedScore(session);

const PathfinderEntry = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isLoggedIn, loading: authLoading, user } = useAuth();
  const { showLoginModal } = useLoginRequiredModal();
  const isAdmin = ADMIN_ROLES.includes(String(user?.role ?? '').toUpperCase());
  // 최초 1회만 설문을 받고, 이미 완료한 사용자는 점수 결과 화면을 바로 보여주기 위해
  // 완료 여부를 확인하는 동안에는 엔트리 화면을 노출하지 않는다.
  const [checking, setChecking] = useState(true);
  // 이미 설문을 완료한 사용자에게 보여줄 기존 세션(점수) 결과
  const [completedSession, setCompletedSession] = useState(null);

  useEffect(() => {
    if (authLoading) return undefined;

    let alive = true;

    const showEntry = () => {
      if (!alive) return;
      trackPathfinderOnboardingView({
        source: searchParams.get('source') || 'gnb_pathfinder_start',
      });
      setChecking(false);
    };

    // 비로그인 사용자는 온보딩 진입 단계(로그인 모달)에서 처리되므로 그대로 엔트리 노출
    if (!isLoggedIn) {
      showEntry();
      return () => {
        alive = false;
      };
    }

    (async () => {
      try {
        const latestSession = await getLatestPathfinderSession();
        if (!alive) return;
        if (hasCompletedOnboarding(latestSession)) {
          // 이미 설문을 완료한 사용자 → 기존 점수 결과 화면을 노출 (엔트리 미노출)
          setCompletedSession(latestSession);
          setChecking(false);
          return;
        }
      } catch (error) {
        // 세션이 없거나 조회 실패 시에는 신규 사용자로 보고 온보딩을 진행한다.
      }
      showEntry();
    })();

    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, isLoggedIn]);

  if (authLoading || checking) {
    return (
      <div className='flex h-[calc(100dvh-44px)] tabletH:h-[calc(100dvh-74px)] items-center justify-center bg-grayscale-gray01'>
        <p className='typo-b16Strong text-text-secondary'>Loading...</p>
      </div>
    );
  }

  // Retake: 저장된 점수·선택지를 초기화하고 설문을 처음부터 다시 진행
  const handleRetake = () => {
    clearPathfinderDraft();
    navigate('/pathfinder/onboarding');
  };

  const handleNext = () => {
    if (!isLoggedIn) {
      showLoginModal('pathfinder_entry_next');
      return;
    }
    navigate('/pathfinder/onboarding');
  };

  // 이미 설문을 완료한 사용자: 다시 start 진입 시 기존 점수 결과 화면 노출
  if (completedSession) {
    return (
      <PathfinderResult
        result={completedSession}
        goalType={completedSession.goalType}
        isAdmin={isAdmin}
        onRetake={handleRetake}
      />
    );
  }

  // GNB 높이는 모바일 44px, tabletH 이상 74px — 뺄 값도 그에 맞춰야 아래가 잘리지 않는다
  return (
    <div className='flex h-[calc(100dvh-44px)] tabletH:h-[calc(100dvh-74px)] px-4 pt-5 pb-[112px] flex-col justify-center items-center self-stretch overflow-hidden tabletH:px-10 tabletH:pt-8 tabletV:px-[80px] tabletV:pt-10'>
      <style>
        {`
            @keyframes pathfinder-float {
              0%, 100% { transform: translate3d(0, 0, 0); }
              50% { transform: translate3d(0, -12px, 0); }
            }

            @keyframes pathfinder-plane-fly {
              0%, 100% {
                opacity: 0.9;
                transform: translate3d(0, 0, 0) rotate(0deg);
              }
              50% {
                opacity: 1;
                transform: translate3d(24px, -18px, 0) rotate(8deg);
              }
            }

            @keyframes pathfinder-footprint-step {
              0%, 10% {
                opacity: 0;
                transform: scale(0.72) translate3d(0, 8px, 0);
              }
              18%, 76% {
                opacity: 1;
                transform: scale(1) translate3d(0, 0, 0);
              }
              100% {
                opacity: 0;
                transform: scale(1) translate3d(0, 0, 0);
              }
            }

            @keyframes pathfinder-stamp-pop {
              0%, 64%, 100% {
                transform: scale(1) translate3d(0, 0, 0);
              }
              72% {
                transform: scale(1.2) translate3d(0, -12px, 0);
              }
              80% {
                transform: scale(0.94) translate3d(0, 2px, 0);
              }
              88% {
                transform: scale(1) translate3d(0, 0, 0);
              }
            }

            .pathfinder-float {
              animation: pathfinder-float 4.2s ease-in-out infinite;
              will-change: transform;
            }

            .pathfinder-plane-fly {
              animation: pathfinder-plane-fly 3.8s ease-in-out infinite;
              will-change: transform, opacity;
            }

            .pathfinder-footprint-left {
              animation: pathfinder-footprint-step 3.2s ease-in-out infinite;
              transform-origin: center;
              will-change: transform, opacity;
            }

            .pathfinder-footprint-right {
              animation: pathfinder-footprint-step 3.2s ease-in-out 0.45s infinite;
              transform-origin: center;
              will-change: transform, opacity;
            }

            .pathfinder-stamp {
              animation: pathfinder-stamp-pop 4.8s ease-in-out infinite;
              transform-origin: center;
              will-change: transform;
            }

            .pathfinder-stamp-delayed {
              animation-delay: 0.8s;
            }

            @media (prefers-reduced-motion: reduce) {
              .pathfinder-float,
              .pathfinder-plane-fly,
              .pathfinder-footprint-left,
              .pathfinder-footprint-right,
              .pathfinder-stamp {
                animation: none;
              }
            }
          `}
      </style>
      <div className='flex shrink-0 pb-5 justify-center items-center gap-[12px] w-full max-w-[868px] tabletH:pb-8 tabletV:pb-14'>
        <div className='flex flex-col items-start flex-1 min-w-0 gap-2 text-center tabletV:gap-3'>
          <p className='self-stretch text-center text-title3 text-text-primary tabletH:text-h6 tabletV:text-h5'>
            Begin your Korean career journey with Pathfinder
          </p>
          <p className='self-stretch text-center text-b13 text-text-placeholder tabletH:text-b14 tabletV:text-b16'>
            Take the first step to get a peronalized roadmap tailored to your
            unique background
          </p>
        </div>
      </div>
      <div className='flex min-h-0 flex-1 w-full max-w-[868px] mx-auto flex-col items-center justify-center gap-[10px]'>
        <div className='relative w-[min(100%,868px,calc((100dvh_-_400px)_*_1.8429))] aspect-[868/471]'>
          <img
            src={`${process.env.REACT_APP_ASSETS_URL}/Pathfinder/pathfindedefaultimg.svg`}
            alt='pathfindedefaultimg'
            className='pathfinder-float absolute left-[24.54%] top-0 z-10 w-[50.92%] h-auto aspect-[193/162] [filter:drop-shadow(0_4px_16px_rgba(23,20,16,0.08))_drop-shadow(0_16px_40px_rgba(23,20,16,0.08))]'
          />
          <img
            src={`${process.env.REACT_APP_ASSETS_URL}/Pathfinder/rounded.svg`}
            alt='rounded'
            className='absolute left-[30.53%] top-[52.76%] w-[5.99%] h-auto aspect-[52/63]'
          />
          <img
            src={`${process.env.REACT_APP_ASSETS_URL}/Pathfinder/stamp.svg`}
            alt='stamp'
            className='pathfinder-stamp absolute left-[24.83%] top-[10.7%] w-[12.24%] h-auto aspect-[106.222/60.699] opacity-50'
          />
          <img
            src={`${process.env.REACT_APP_ASSETS_URL}/Pathfinder/tail.svg`}
            alt='tail'
            className='absolute left-[67.33%] top-[22.08%] w-[8.38%] h-auto aspect-[72.755/59.244] rotate-[5.739deg]'
          />
          <img
            src={`${process.env.REACT_APP_ASSETS_URL}/Pathfinder/oinglablogo-wave.svg`}
            alt='oinglablogo-wave'
            className='absolute left-[63.98%] top-[51.8%] w-[12.24%] h-auto aspect-[106.26/76.53] opacity-80'
          />
          <img
            src={`${process.env.REACT_APP_ASSETS_URL}/Pathfinder/img-airplane.svg`}
            alt='img-airplane'
            className='pathfinder-plane-fly absolute left-[73.78%] top-[10.67%] w-[7.68%] h-auto aspect-[66.68/64.48]'
          />
          <img
            src={`${process.env.REACT_APP_ASSETS_URL}/Pathfinder/foot-left.svg`}
            alt='foot-left'
            className='pathfinder-footprint-left absolute left-[27.72%] top-[64.88%] w-[8.4%] h-auto aspect-[1/1]'
          />
          <img
            src={`${process.env.REACT_APP_ASSETS_URL}/Pathfinder/foot-right.svg`}
            alt='foot-right'
            className='pathfinder-footprint-right absolute left-[32.01%] top-[65.94%] w-[8.4%] h-auto aspect-[1/1]'
          />
          <img
            src={`${process.env.REACT_APP_ASSETS_URL}/Kpathport/img-kpathport-oval-cat.svg`}
            alt='img-logo-oval-cat'
            className='pathfinder-stamp pathfinder-stamp-delayed absolute left-[16.04%] top-[18.26%] w-[13.94%] h-auto aspect-[121/87]'
          />
        </div>
      </div>

      <div className='fixed bottom-0 left-0 z-30 flex min-h-[100px] w-full items-start justify-center bg-grayscale-gray00 px-4 pt-6 pb-[calc(env(safe-area-inset-bottom,0px)+24px)] tabletH:px-20'>
        <BaseButton
          type='primary'
          size='xl'
          width='min(570px, 100%)'
          hideIcon
          onClick={handleNext}
        >
          Next
        </BaseButton>
      </div>
    </div>
  );
};

export default PathfinderEntry;
